import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';

import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  updateTokens
} from '@/features/auth/context/tokenStorage';
import i18n from '@/i18n';
import { getOrCreateUserAppUuid } from '@/utils/userAppUuid';

// Timeout konstante — centralno mjesto za sve izmjene
const TIMEOUT_DEFAULT_MS = 30_000; // 30s — standardni API pozivi
const TIMEOUT_AUTH_MS = 10_000; // 10s — login/logout/refresh (kratko, brzo treba odgovoriti)

export const axiosInstance = axios.create({
  // Lokalno (dev): VITE_API_BASE_URL je prazan → Axios šalje relativne putanje
  //   → Vite proxy hvata /api/* i prosljeđuje na backend (nema CORS problema)
  // AWS (test/prod): VITE_API_BASE_URL je puni URL backenda
  //   → Axios šalje direktno na backend URL
  baseURL: import.meta.env.VITE_API_BASE_URL || undefined,
  headers: { 'Content-Type': 'application/json' },
  timeout: TIMEOUT_DEFAULT_MS
});

// Auth endpointi dobivaju kraći timeout — override po requestu
axiosInstance.interceptors.request.use((config) => {
  const url = config.url ?? '';
  if (url.includes('/api/v1/auth/')) {
    config.timeout = TIMEOUT_AUTH_MS;
  }
  return config;
});

// Attach token (session or local, whichever holds the current session) to every request
axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Attach the active i18next locale as Accept-Language to EVERY request — backend
// koristi ovo za lokalizirane API/email poruke (vidi WebMvcConfig.localeResolver()).
// Nezavisno od `locale` polja koje login/refresh šalju u bodyju (to je samo za access_log audit).
axiosInstance.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18n.language;
  return config;
});

// Stabilan per-browser UUID na svaki zahtjev — backend ga upisuje u MDC
// (uz requestId/userId/organisationId) i u access_log, radi korelacije
// više zahtjeva ISTOG browsera kroz vrijeme.
axiosInstance.interceptors.request.use((config) => {
  config.headers['X-User-App-Uuid'] = getOrCreateUserAppUuid();
  return config;
});

// On 401 — pokušaj refresh, ako ne uspije redirect na login. Ovo je isključivo
// za "pravi" API pozivi čiji je access token istekao usred sesije — NIJEDAN
// /api/v1/auth/* poziv ne smije ući ovamo, jer 401 ondje uvijek znači nešto
// specifično toj akciji (krivi podaci za login, nevažeći/istekao/iskorišten
// token za verify-email/accept-invite/reset-password, kriva trenutna lozinka
// za change-password) — nikad "moj access token je istekao, treba refresh".
// Svaki takav poziv već ima svoj vlastiti error handling (useAuth, ResetPasswordForm...).
const isAuthEndpoint = (url: string): boolean => url.includes('/api/v1/auth/');

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };
    const url = originalRequest?.url ?? '';

    if (error.response?.status === 401 && !isAuthEndpoint(url) && !originalRequest._retry) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearSession();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((newToken) => {
            originalRequest!.headers!.Authorization = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest!));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const locale = localStorage.getItem('i18nextLng') ?? 'en';
        const { data } = await axiosInstance.post<{
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
        }>('/api/v1/auth/refresh', { refreshToken, locale });
        updateTokens(data.accessToken, data.refreshToken);
        refreshQueue.forEach((cb) => cb(data.accessToken));
        refreshQueue = [];
        originalRequest!.headers!.Authorization = `Bearer ${data.accessToken}`;
        return axiosInstance(originalRequest!);
      } catch {
        clearSession();
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Mutator za orval generiran klijent
export const apiClient = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance(config).then((r) => r.data);
